<template>
    <v-container fluid class="product-v2">
        <!-- Barcode Scanner Button -->
        <v-row v-if="fromBarcode" class="mb-4">
            <v-col class="text-center">
                <v-btn color="primary" :to="{ name: 'product-barcodescanner' }">
                    <v-icon left>mdi-barcode</v-icon>
                    Scan Barcode
                </v-btn>
            </v-col>
        </v-row>

        <!-- Toggle vista V1/V2 -->
        <v-row v-if="product" class="mb-2">
            <v-col class="text-right">
                <v-btn
                    small
                    outlined
                    color="primary"
                    :to="{
                        name: 'product-v1',
                        query: {
                            id: product.ar_codart,
                            fromBarcode: $route.query.fromBarcode,
                        },
                    }"
                >
                    <v-icon left small>mdi-table</v-icon>
                    Vista classica (V1)
                </v-btn>
            </v-col>
        </v-row>

        <!-- Main Product Information -->
        <v-row v-if="product">
            <!-- Product Image -->
            <v-col md="4" sm="12">
                <v-card class="product-image-card">
                    <ProductImage
                        :product-id="product.ar_codart"
                        :height="310"
                    ></ProductImage>
                </v-card>
            </v-col>

            <!-- Product Details -->
            <v-col md="8" sm="12">
                <v-card class="product-details-card">
                    <!-- Product Header -->
                    <v-card-title class="product-header">
                        <div class="product-title">
                            <h2>{{ product.ar_codart }} - {{ product.ar_descr }}</h2>
                            <p class="product-category">{{ product.acl_descla1 }} → {{ product.acl_descla2 }}</p>
                        </div>
                    </v-card-title>

                    <v-card-text>
                        <!-- Pricing Section -->
                        <div class="section-container mb-6">
                            <div class="section-title">
                                <v-icon left color="primary">mdi-currency-eur</v-icon>
                                Prezzi
                            </div>
                            <div class="section-content">
                                <v-row>
                                    <v-col cols="6" md="3" v-if="product.lc_prezzo6">
                                        <div class="price-item">
                                            <div class="price-label">Dollaro</div>
                                            <div class="price-value">{{ product.lc_prezzo6 | formatDollaro }}</div>
                                        </div>
                                    </v-col>
                                    <v-col
                                        cols="6"
                                        md="3"
                                        v-if="product.lc_prezzo8 && (!productStats || productStats.USER.role != 'Franchise')"
                                    >
                                        <div class="price-item discount-item">
                                            <div class="price-label">Prz Li8</div>
                                            <div class="price-calculation">
                                                <span class="base-price">{{ product.lc_prezzo8 | formatEuro }}</span>
                                                <span class="discount" v-if="product.so_sconto1l8">-{{ product.so_sconto1l8 }}%</span>
                                                <span class="final-price" v-if="product.lc_prezzonetto8">= {{ product.lc_prezzonetto8 | formatEuro }}</span>
                                            </div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="3" v-if="product.lc_prezzo5">
                                        <div class="price-item">
                                            <div class="price-label">Prz Li5</div>
                                            <div class="price-value">{{ product.lc_prezzo5 | formatEuro }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="3" v-if="product.lc_prezzo1">
                                        <div class="price-item">
                                            <div class="price-label">Prz Ing.</div>
                                            <div class="price-value">{{ product.lc_prezzo1 | formatEuro }}</div>
                                        </div>
                                    </v-col>
                                </v-row>

                                <v-row v-if="product.so_sconto2l3">
                                    <v-col cols="12" md="6">
                                        <div class="price-item discount-item">
                                            <div class="price-label">Prz Franch.</div>
                                            <div class="price-calculation">
                                                <span class="base-price">{{ product.lc_prezzo2 | formatEuro }}</span>
                                                <span class="discount">-{{ product.so_sconto2l3 }}%</span>
                                                <span class="final-price">= {{ calcoloScontoFrancising(product) | formatEuro }}</span>
                                            </div>
                                        </div>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <div class="price-item discount-item">
                                            <div class="price-label">Prz Ven Negozio</div>
                                            <div class="price-calculation">
                                                <span class="base-price">{{ product.lc_prezzo2 | formatEuro }}</span>
                                                <span class="discount">-{{ product.so_sconto1l2 }}%</span>
                                                <span class="final-price">= {{ calcoloScontoNegozi(product) | formatEuro }}</span>
                                            </div>
                                        </div>
                                    </v-col>
                                </v-row>
                            </div>
                        </div>

                        <!-- Supplier Information -->
                        <div class="section-container mb-6">
                            <div class="section-title">
                                <v-icon left color="primary">mdi-truck</v-icon>
                                Fornitore
                            </div>
                            <div class="section-content">
                                <v-row>
                                    <v-col cols="8">
                                        <div class="info-item">
                                            <span class="info-label">Fornitore:</span>
                                            <router-link
                                                :to="{
                                                    name: 'product-list',
                                                    query: { cod_supplier: product.ar_codforn }
                                                }"
                                                class="supplier-link"
                                            >
                                                {{ product.ar_codforn }}
                                            </router-link>
                                            <span v-if="product.an_descr1"> - {{ product.an_descr1 }}</span>
                                        </div>
                                    </v-col>
                                    <v-col cols="4">
                                        <div class="info-item">
                                            <span class="info-label">Cod.Art.F:</span>
                                            <span class="info-value">{{ product.xx_codarfo }}</span>
                                        </div>
                                    </v-col>
                                </v-row>
                            </div>
                        </div>

                        <!-- Product Specifications -->
                        <div class="section-container mb-6">
                            <div class="section-title">
                                <v-icon left color="primary">mdi-package-variant</v-icon>
                                Specifiche Prodotto
                            </div>
                            <div class="section-content">
                                <v-row>
                                    <v-col cols="6" md="2">
                                        <div class="spec-item">
                                            <div class="spec-label">Min.Acq.</div>
                                            <div class="spec-value">{{ product.ar_minordinabile }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="2">
                                        <div class="spec-item">
                                            <div class="spec-label">Inner</div>
                                            <div class="spec-value">{{ product.ar_inner }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="2">
                                        <div class="spec-item">
                                            <div class="spec-label">Cart</div>
                                            <div class="spec-value">{{ product.ar_cartonaggio }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="2" v-if="product.ar_volume">
                                        <div class="spec-item">
                                            <div class="spec-label">CBM</div>
                                            <div class="spec-value">{{ product.ar_volume }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="2" v-if="product.xx_lottoriordino">
                                        <div class="spec-item">
                                            <div class="spec-label">Lotto riordino</div>
                                            <div class="spec-value">{{ product.xx_lottoriordino }}</div>
                                        </div>
                                    </v-col>
                                </v-row>
                            </div>
                        </div>

                        <!-- Product Dimensions -->
                        <div class="dimensions-compact mb-4">
                            <div class="dimensions-title">
                                <v-icon small color="grey">mdi-ruler</v-icon>
                                <span>Dimensioni:</span>
                            </div>
                            <div class="dimensions-values">
                                <span class="dimension-compact">
                                    <v-icon x-small color="grey" class="mr-1">mdi-arrow-up-down</v-icon>
                                    {{ product.ar_misura1 }}cm
                                </span>
                                <span class="dimension-compact">
                                    <v-icon x-small color="grey" class="mr-1">mdi-arrow-left-right</v-icon>
                                    {{ product.ar_misura2 }}cm
                                </span>
                                <span class="dimension-compact">
                                    <v-icon x-small color="grey" class="mr-1">mdi-arrow-expand-all</v-icon>
                                    {{ product.ar_misura3 }}cm
                                </span>
                            </div>
                        </div>

                        <!-- Additional Information -->
                        <div class="section-container mb-6">
                            <div class="section-title">
                                <v-icon left color="primary">mdi-information</v-icon>
                                Informazioni Aggiuntive
                            </div>
                            <div class="section-content">
                                <v-row>
                                    <v-col cols="6">
                                        <div class="info-item">
                                            <span class="info-label">Data Carico:</span>
                                            <router-link
                                                :to="{
                                                    name: 'product-list',
                                                    query: {
                                                        cod_supplier: product.ar_codforn,
                                                        data_carico: product.xx_jac_primadatacm
                                                    }
                                                }"
                                                class="date-link"
                                            >
                                                {{ product.xx_jac_primadatacm | formatDada }}
                                            </router-link>
                                        </div>
                                    </v-col>
                                    <v-col cols="6">
                                        <div class="info-item">
                                            <span class="info-label">EAN:</span>
                                            <span class="info-value">{{ product.bc_code }}</span>
                                        </div>
                                    </v-col>
                                </v-row>
                            </div>
                        </div>

                        <!-- Ubicazione e Disponibilità -->
                        <div class="section-container mb-6">
                            <div class="section-title">
                                <v-icon left color="primary">mdi-map-marker</v-icon>
                                Ubicazione e Disponibilità
                            </div>
                            <div class="section-content">
                                <v-row>
                                    <v-col cols="6" md="3">
                                        <div class="spec-item">
                                            <div class="spec-label">Ubicazione</div>
                                            <div class="spec-value">{{ product.ar_ubicus }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="3">
                                        <div class="spec-item">
                                            <div class="spec-label">Stoccaggio</div>
                                            <div class="spec-value">{{ product.ar_ubicst }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="2">
                                        <div class="spec-item">
                                            <div class="spec-label">Stk</div>
                                            <div class="spec-value">{{ product.xx_dissede }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="2">
                                        <div class="spec-item">
                                            <div class="spec-label">Disp. netta</div>
                                            <div class="spec-value">{{ product.xx_dissedeconimpegni }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6" md="2">
                                        <div class="spec-item">
                                            <div class="spec-label">Impegnato</div>
                                            <div class="spec-value">{{ impegnatoCasaMadre(product) }}</div>
                                        </div>
                                    </v-col>
                                </v-row>
                            </div>
                        </div>

                        <!-- Admin Section -->
                        <div v-if="productStats && productStats.USER.role == 'Admin'" class="section-container mb-6">
                            <div class="section-title">
                                <v-icon left color="primary">mdi-chart-box</v-icon>
                                Statistiche Amministratore
                            </div>
                            <div class="section-content admin-section">
                                <v-row>
                                    <v-col cols="6">
                                        <div class="admin-stat">
                                            <div class="stat-label">Carico Iniziale</div>
                                            <div class="stat-value">{{ productStats.SEDE.CARICO }}</div>
                                            <div class="stat-euro">{{ (productStats.SEDE.CARICO * product.lc_prezzo5) | formatEuro }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="6">
                                        <div class="admin-stat">
                                            <div class="stat-label">Stock Attuale</div>
                                            <div class="stat-value">{{ product.xx_dissede }}</div>
                                        </div>
                                    </v-col>
                                </v-row>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Statistics Tables -->
        <v-row v-if="productStats">
            <!-- Franchise Statistics -->
            <v-col cols="12" v-if="statsFranchise.length > 0">
                <v-card class="stats-card">
                    <v-card-title class="stats-title">
                        <v-icon left color="primary">mdi-store</v-icon>
                        FRANCHISE
                    </v-card-title>
                    <v-card-text>
                        <v-data-table
                            :headers="franchiseHeaders"
                            :items="statsFranchise"
                            dense
                            hide-default-footer
                            disable-pagination
                            class="stats-table"
                        >
                            <template v-slot:item.CARICO.date="{ item }">
                                {{ item.CARICO.date | formatDada }}
                            </template>
                            <template v-slot:item.totaleSale="{ item }" v-if="productStats.USER.role == 'Admin'">
                                {{ (item.CARICO.XX_QTACARICATA * calcoloScontoFrancising(product)) | formatEuro }}
                            </template>
                            <template v-slot:item.percentage="{ item }">
                                {{ calcoloPercentuale(product, item.CARICO.XX_QTACARICATA) | arrotonda(2) }}%
                            </template>
                        </v-data-table>

                        <!-- Franchise Totals -->
                        <v-card class="mt-4 totals-card">
                            <v-card-text>
                                <v-row class="totals-row">
                                    <v-col>
                                        <div class="total-item">
                                            <span class="total-label">Totale Carico:</span>
                                            <span class="total-value">{{ totCarFranchise }}</span>
                                        </div>
                                    </v-col>
                                    <v-col>
                                        <div class="total-item">
                                            <span class="total-label">Totale Vendite:</span>
                                            <span class="total-value">{{ totVendFranchise }}</span>
                                        </div>
                                    </v-col>
                                    <v-col>
                                        <div class="total-item">
                                            <span class="total-label">Totale Giacenze:</span>
                                            <span class="total-value">{{ totGiacFranchise }}</span>
                                        </div>
                                    </v-col>
                                    <v-col v-if="productStats.USER.role == 'Admin'">
                                        <div class="total-item">
                                            <span class="total-label">Totale Vendita:</span>
                                            <span class="total-value">{{ totVenditaEuroFranchise | formatEuro }}</span>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Shop Statistics -->
            <v-col cols="12" v-if="statsNegozi.length > 0">
                <v-card class="stats-card">
                    <v-card-title class="stats-title">
                        <v-icon left color="primary">mdi-storefront</v-icon>
                        NEGOZI
                    </v-card-title>
                    <v-card-text>
                        <v-data-table
                            :headers="negoziHeaders"
                            :items="statsNegozi"
                            dense
                            hide-default-footer
                            disable-pagination
                            class="stats-table"
                        >
                            <template v-slot:item.CARICO.date="{ item }">
                                {{ item.CARICO.date | formatDada }}
                            </template>
                            <template v-slot:item.totaleSale="{ item }" v-if="productStats.USER.role == 'Admin'">
                                {{ (item.VENDUTO.XX_QTAVENDUTA * calcoloScontoNegozi(product)) | formatEuro }}
                            </template>
                            <template v-slot:item.percentage="{ item }">
                                {{ calcoloPercentuale(product, item.VENDUTO.XX_QTAVENDUTA) | arrotonda(2) }}%
                            </template>
                        </v-data-table>

                        <!-- Shop Totals -->
                        <v-card class="mt-4 totals-card">
                            <v-card-text>
                                <v-row class="totals-row">
                                    <v-col>
                                        <div class="total-item">
                                            <span class="total-label">Totale Carico:</span>
                                            <span class="total-value">{{ totCarShop }}</span>
                                        </div>
                                    </v-col>
                                    <v-col>
                                        <div class="total-item">
                                            <span class="total-label">Totale Vendite:</span>
                                            <span class="total-value">{{ totVendShop }}</span>
                                        </div>
                                    </v-col>
                                    <v-col>
                                        <div class="total-item">
                                            <span class="total-label">Totale Giacenze:</span>
                                            <span class="total-value">{{ totGiacShop }}</span>
                                        </div>
                                    </v-col>
                                    <v-col v-if="productStats.USER.role == 'Admin'">
                                        <div class="total-item">
                                            <span class="total-label">Totale Vendita:</span>
                                            <span class="total-value">{{ totVenditaEuroNegozi | formatEuro }}</span>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Grand Total -->
            <v-col cols="12" v-if="statsFranchise.length > 0">
                <v-card class="grand-total-card">
                    <v-card-title class="grand-total-title">
                        <v-icon left color="success">mdi-sigma</v-icon>
                        SUPER TOTALE
                    </v-card-title>
                    <v-card-text>
                        <v-row class="grand-totals-row">
                            <v-col>
                                <div class="grand-total-item">
                                    <div class="grand-total-label">Carico</div>
                                    <div class="grand-total-value">{{ totCarShop + totCarFranchise }}</div>
                                </div>
                            </v-col>
                            <v-col>
                                <div class="grand-total-item">
                                    <div class="grand-total-label">Vendite</div>
                                    <div class="grand-total-value">{{ totVendShop + totVendFranchise }}</div>
                                </div>
                            </v-col>
                            <v-col>
                                <div class="grand-total-item">
                                    <div class="grand-total-label">Giacenze</div>
                                    <div class="grand-total-value">{{ totGiacShop + totGiacFranchise }}</div>
                                </div>
                            </v-col>
                            <v-col v-if="productStats.USER.role == 'Admin'">
                                <div class="grand-total-item">
                                    <div class="grand-total-label">Totale</div>
                                    <div class="grand-total-value">{{ (totVenditaEuroNegozi + totVenditaEuroFranchise) | formatEuro }}</div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Loading State -->
        <v-row v-else>
            <v-col class="text-center">
                <v-progress-circular
                    indeterminate
                    color="primary"
                    size="64"
                ></v-progress-circular>
                <p class="mt-4 text-h6">Caricamento dati prodotto...</p>
            </v-col>
        </v-row>
    </v-container>
</template>

<style lang="scss" scoped>
.product-v2 {
    .product-image-card {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .product-details-card {
        height: 100%;
    }

    .product-header {
        background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
        color: white;
        padding: 24px;

        .product-title {
            flex: 1;

            h2 {
                font-size: 1.5rem;
                font-weight: 600;
                margin-bottom: 8px;
            }

            .product-category {
                opacity: 0.9;
                font-size: 0.9rem;
                margin: 0;
            }
        }

    }

    .section-container {
        background: #fafafa;
        border-radius: 8px;
        padding: 20px;

        .admin-section {
            background: transparent;
            border-radius: 8px;
            padding: 0;
        }
    }

    .section-title {
        font-weight: 600;
        font-size: 1.1rem;
        color: #1976d2;
        padding-bottom: 8px;
        border-bottom: 2px solid #e3f2fd;
        margin-bottom: 16px;
    }

    .section-content {
        margin-top: 16px;
    }

    .dimensions-compact {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 12px 16px;
        background: #f5f5f5;
        border-radius: 6px;
        border-left: 3px solid #e0e0e0;

        .dimensions-title {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #666;
            font-size: 0.9rem;
            font-weight: 500;
            min-width: fit-content;
        }

        .dimensions-values {
            display: flex;
            gap: 12px;
            flex-wrap: wrap;

            .dimension-compact {
                display: flex;
                align-items: center;
                color: #555;
                font-size: 0.85rem;
                background: white;
                padding: 4px 8px;
                border-radius: 4px;
                border: 1px solid #e0e0e0;
            }
        }
    }

    .price-item {
        text-align: center;
        padding: 16px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        background: #fafafa;
        transition: all 0.2s;

        &:hover {
            background: #f5f5f5;
            border-color: #1976d2;
        }

        .price-label {
            font-size: 0.85rem;
            color: #666;
            margin-bottom: 8px;
            font-weight: 500;
        }

        .price-value {
            font-size: 1.25rem;
            font-weight: 600;
            color: #1976d2;
        }

        &.discount-item {
            .price-calculation {
                display: flex;
                flex-direction: column;
                gap: 4px;

                .base-price {
                    font-size: 0.9rem;
                    color: #666;
                }

                .discount {
                    color: #f44336;
                    font-weight: 600;
                }

                .final-price {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #4caf50;
                }
            }
        }
    }

    .info-item, .spec-item {
        margin-bottom: 12px;

        .info-label, .spec-label {
            font-weight: 600;
            color: #666;
            display: block;
            margin-bottom: 4px;
            font-size: 0.85rem;
        }

        .info-value, .spec-value {
            font-size: 1rem;
            color: #333;
        }

        .supplier-link, .date-link {
            color: #1976d2;
            text-decoration: none;
            font-weight: 500;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    .spec-item {
        text-align: center;
        padding: 12px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        background: #fafafa;

        .spec-label {
            color: #666;
            font-size: 0.8rem;
            margin-bottom: 8px;
        }

        .spec-value {
            font-size: 1.1rem;
            font-weight: 600;
            color: #333;
        }
    }

    .admin-stat {
        text-align: center;
        padding: 16px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);

        .stat-label {
            font-size: 0.85rem;
            color: #666;
            margin-bottom: 8px;
            display: block;
        }

        .stat-value {
            font-size: 1.5rem;
            font-weight: 600;
            color: #ff6f00;
            display: block;
        }

        .stat-euro {
            font-size: 1rem;
            color: #4caf50;
            font-weight: 500;
            margin-top: 4px;
        }
    }

    .stats-card {
        margin-bottom: 24px;

        .stats-title {
            background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
            color: white;
            padding: 16px 24px;
        }

        .stats-table {
            ::v-deep .v-data-table__wrapper {
                border: 1px solid #e0e0e0;
                border-radius: 8px;
            }

            ::v-deep th {
                background: #f5f5f5;
                font-weight: 600;
                color: #333;
            }

            ::v-deep tr:nth-child(even) {
                background: #fafafa;
            }
        }
    }

    .totals-card {
        background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);

        .totals-row {
            margin: 0;

            .total-item {
                text-align: center;

                .total-label {
                    font-size: 0.9rem;
                    color: #666;
                    display: block;
                    margin-bottom: 8px;
                }

                .total-value {
                    font-size: 1.3rem;
                    font-weight: 600;
                    color: #1976d2;
                }
            }
        }
    }

    .grand-total-card {
        background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
        border: 2px solid #4caf50;

        .grand-total-title {
            background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
            color: white;
            padding: 20px 24px;
        }

        .grand-totals-row {
            margin: 0;

            .grand-total-item {
                text-align: center;
                padding: 16px;

                .grand-total-label {
                    font-size: 1rem;
                    color: #666;
                    font-weight: 600;
                    display: block;
                    margin-bottom: 12px;
                }

                .grand-total-value {
                    font-size: 1.8rem;
                    font-weight: 700;
                    color: #2e7d32;
                }
            }
        }
    }
}
</style>

<script>
import requests from "../requests";
import ProductImage from "./ProductImage.vue";
import Promise from "promise";

export default {
    name: "ProductV2",
    components: { ProductImage },

    computed: {
        franchiseHeaders() {
            const headers = [
                { text: 'Neg.', value: 'description', sortable: false },
                { text: 'Data doc.', value: 'CARICO.date', sortable: false },
                { text: 'Car.', value: 'CARICO.XX_QTACARICATA', sortable: false },
                { text: 'Vend.', value: 'VENDUTO.XX_QTAVENDUTA', sortable: false },
                { text: 'Giac.', value: 'GIACENZA.XX_GIACENZAFIN', sortable: false },
                { text: '% Intero', value: 'percentage', sortable: false }
            ];

            if (this.productStats && this.productStats.USER.role === 'Admin') {
                headers.splice(5, 0, { text: 'Totale vendita', value: 'totaleSale', sortable: false });
            }

            return headers;
        },

        negoziHeaders() {
            const headers = [
                { text: 'Neg.', value: 'description', sortable: false },
                { text: 'Data doc.', value: 'CARICO.date', sortable: false },
                { text: 'Car.', value: 'CARICO.XX_QTACARICATA', sortable: false },
                { text: 'Vend.', value: 'VENDUTO.XX_QTAVENDUTA', sortable: false },
                { text: 'Giac.', value: 'GIACENZA.XX_GIACENZAFIN', sortable: false },
                { text: '% Intero', value: 'percentage', sortable: false }
            ];

            if (this.productStats && this.productStats.USER.role === 'Admin') {
                headers.splice(5, 0, { text: 'Totale vendita', value: 'totaleSale', sortable: false });
            }

            return headers;
        },

        totCar() {
            return this.calcolaSomma("carico");
        },
        totVend() {
            return this.calcolaSomma("scarico");
        },
        totGiac() {
            return this.calcolaSomma("giacenza");
        },

        totCarShop() {
            return this.calcolaSomma("carico", "shop");
        },
        totVendShop() {
            return this.calcolaSomma("scarico", "shop");
        },
        totGiacShop() {
            return this.calcolaSomma("giacenza", "shop");
        },

        totCarFranchise() {
            return this.calcolaSomma("carico", "franchise");
        },
        totVendFranchise() {
            return this.calcolaSomma("scarico", "franchise");
        },
        totGiacFranchise() {
            return this.calcolaSomma("giacenza", "franchise");
        },

        totVenditaEuroNegozi() {
            let shopsStats = _.filter(this.productStats.NEGOZI, {
                type: "shop",
            });
            let somma = 0;
            _.forEach(shopsStats, (shopStat) => {
                if (_.isNumber(shopStat.VENDUTO.XX_QTAVENDUTA))
                    somma +=
                        shopStat.VENDUTO.XX_QTAVENDUTA *
                        this.calcoloScontoNegozi(this.product);
            });
            return somma;
        },

        totVenditaEuroFranchise() {
            let shopsStats = _.filter(this.productStats.NEGOZI, {
                type: "franchise",
            });
            let somma = 0;
            _.forEach(shopsStats, (shopStat) => {
                if (_.isNumber(shopStat.CARICO.XX_QTACARICATA))
                    somma +=
                        shopStat.CARICO.XX_QTACARICATA *
                        this.calcoloScontoFrancising(this.product);
            });
            return somma;
        },

        franchisePos() {
            return _.filter(this.pointOfSales, { type: "franchise" });
        },
        standardShop() {
            return _.filter(this.pointOfSales, { type: "shop" });
        },

        statsNegozi() {
            if(this.user.role == "Solo Prodotti") return [];
            return _.filter(this.productStats.NEGOZI, { type: "shop" });
        },
        statsFranchise() {
            return _.filter(this.productStats.NEGOZI, { type: "franchise" });
        },
    },

    data: () => {
        return {
            product: null,
            productStats: null,
            fromBarcode: false,
            pointOfSales: [],
            posField: {},
            user: null
        };
    },

    mounted() {
        console.log("ProductV2 Mounted.");
        this.user = window.currentUser;
        let def0 = requests.getProduct(this.$route.query.id);
        let def1 = requests.getProductSellStats(this.$route.query.id);

        this.fromBarcode = false;
        if (
            !_.isUndefined(this.$route.query.fromBarcode) &&
            this.$route.query.fromBarcode == 1
        )
            this.fromBarcode = true;

        Promise.all([def0, def1]).then((res) => {
            this.product = res[0].data[0];
            this.productStats = res[1].data;
        });
    },

    methods: {
        calcoloScontoFrancising(product) {
            let prezzo = product.lc_prezzo2;
            let sconto1 = product.so_sconto1l3;
            let sconto2 = product.so_sconto2l3;
            let ris = prezzo - (prezzo * sconto1) / 100;
            return ris - (ris * sconto2) / 100;
        },
        calcoloScontoNegozi(product) {
            let prezzo = product.lc_prezzo2;
            let sconto1 = product.so_sconto1l2;
            return prezzo - (prezzo * sconto1) / 100;
        },
        impegnatoCasaMadre(product) {
            if (!_.isUndefined(product.xx_impegnato) && !_.isNull(product.xx_impegnato))
                return product.xx_impegnato;
            let stk = parseInt(product.xx_dissede);
            let netto = parseInt(product.xx_dissedeconimpegni);
            if (_.isNaN(stk) || _.isNaN(netto)) return 0;
            return stk - netto;
        },
        calcoloPercentuale(product, giacenza) {
            let num =
                ((giacenza * this.calcoloScontoNegozi(product)) /
                    (this.calcoloScontoNegozi(product) * this.totVend)) *
                100;
            if (_.isNaN(num)) return 0;
            return num;
        },
        calcolaSomma(type, shopType = "all") {
            let shopsStats = this.productStats.NEGOZI;
            if (shopType != "all") {
                shopsStats = _.filter(this.productStats.NEGOZI, {
                    type: shopType,
                });
            }

            let somma = 0;
            if (type == "carico") {
                _.forEach(shopsStats, (val) => {
                    if (_.isNumber(val.CARICO.XX_QTACARICATA))
                        somma += val.CARICO.XX_QTACARICATA;
                });
            }

            if (type == "scarico") {
                _.forEach(shopsStats, (val) => {
                    if (_.isNumber(val.VENDUTO.XX_QTAVENDUTA))
                        somma += val.VENDUTO.XX_QTAVENDUTA;
                });
            }

            if (type == "giacenza") {
                _.forEach(shopsStats, (val) => {
                    if (_.isNumber(val.GIACENZA.XX_GIACENZAFIN))
                        somma += val.GIACENZA.XX_GIACENZAFIN;
                });
            }
            return somma;
        },
        helperShopFields(shopId, field) {
            return this.product[this.posField[shopId][field]];
        },
    },

    filters: {
        formatEuro(number) {
            if (_.isNaN(number)) return 0;
            return new Intl.NumberFormat("it-IT", {
                style: "currency",
                currency: "EUR",
            }).format(number);
        },
        formatDollaro(number) {
            return new Intl.NumberFormat("us-US", {
                style: "currency",
                currency: "USD",
            }).format(number);
        },
        arrotonda(data, num) {
            let molt = 1;
            for (let i = 0; i < num; i++) {
                molt *= 10;
            }
            return Math.round(data * molt) / molt;
        },
        formatDada(data) {
            if (
                _.isUndefined(data) ||
                _.isNull(data) ||
                data == "1900-01-01T00:00:00"
            )
                return null;

            let dsplit = data.split("T")[0].split("-");
            let d = new Date(
                parseInt(dsplit[0]),
                parseInt(dsplit[1]) - 1,
                parseInt(dsplit[2])
            );
            return d.toLocaleString("it-IT", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            });
        },
    },
};
</script>